import { useState } from "react";
import { useAtomValue } from "jotai";
import { Check, Copy } from "lucide-react";
import { localeAtom } from "../../stores/locale";
import { useT } from "../../lib/i18n";
import { eisAgentPrompt } from "./utils/eisAgentPrompt";

// EisYamlAiGuide surfaces the "let an AI agent write your eis.yaml" path: the
// user runs a coding agent (Claude Code etc.) inside their repo, pastes the
// prompt below, and the agent observes the repo and emits a tailored eis.yaml
// they then upload. Shared by Settings (the eis.yaml box) and the onboarding
// `signaling` step so the copy/prompt lives in exactly one place. `uploadHint`
// lets the host override step 4's wording (Settings points at "this page's
// Upload"; onboarding points at the Settings page) without forking the steps.
type Props = {
  uploadHint?: string;
};

export default function EisYamlAiGuide({ uploadHint }: Props) {
  const locale = useAtomValue(localeAtom);
  const t = useT(locale);
  const [copied, setCopied] = useState(false);

  const copyPrompt = () => {
    navigator.clipboard.writeText(eisAgentPrompt(locale));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const steps = [
    t("settings.eisAgent.step1"),
    t("settings.eisAgent.step2"),
    t("settings.eisAgent.step3"),
    uploadHint ?? t("settings.eisAgent.step4"),
  ];

  return (
    <div className="flex flex-col gap-md rounded-md border border-gruvbox-bg3 bg-gruvbox-bg p-lg">
      <div className="flex flex-col gap-xs">
        <h4 className="text-sm font-medium text-gruvbox-fg1">
          {t("settings.eisAgent.title")}
        </h4>
        <p className="text-xs text-gruvbox-gray">{t("settings.eisAgent.desc")}</p>
      </div>

      <ol className="flex flex-col gap-sm">
        {steps.map((stepText, i) => (
          <li key={stepText} className="flex gap-md text-xs text-gruvbox-fg1">
            <span className="shrink-0 text-gruvbox-aqua tabular-nums">{i + 1}.</span>
            <span>{stepText}</span>
          </li>
        ))}
      </ol>

      <div className="rounded-md border border-gruvbox-bg3 bg-gruvbox-bg1">
        <div className="flex items-center justify-between border-b border-gruvbox-bg3 px-md py-sm">
          <span className="text-[10px] uppercase tracking-wide text-gruvbox-gray">
            eis.yaml prompt
          </span>
          <button
            type="button"
            onClick={copyPrompt}
            className="flex items-center gap-xs rounded bg-gruvbox-bg3 px-md py-sm text-xs text-gruvbox-fg1 transition-colors hover:bg-gruvbox-bg2"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? t("settings.eisAgent.copied") : t("settings.eisAgent.copy")}
          </button>
        </div>
        <pre className="max-h-[16rem] overflow-auto whitespace-pre-wrap break-words px-md py-sm font-mono text-[11px] leading-relaxed text-gruvbox-fg1">
          {eisAgentPrompt(locale)}
        </pre>
      </div>
    </div>
  );
}
